import React, { FC, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Stack,
  Icon,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import { IngredientOption } from "../types";
import dayjs from "dayjs";
import {
  convertUnits,
  formatUnitValue,
  suggestAlternativeUnit,
  areUnitsCompatible,
} from "../utils/units";

interface IngredientCardProps {
  ingredient: {
    item_id: string;
    item_type: "ingredient" | "supply";
    quantity: number;
    unit: string;
    notes?: string;
    ingredient_id: string;
    selected_batch_id?: string;
    brand_name?: string;
  };
  ingredientDetails?: IngredientOption;
  batchDetails?: {
    batch_number: string;
    expiry_date: string;
    remaining_quantity: number;
    quantity: number;
    cost_per_unit: number;
    brand_name?: string;
  };
  onEdit: () => void;
  onDelete: () => void;
}

export const IngredientCard: FC<IngredientCardProps> = ({
  ingredient,
  ingredientDetails,
  batchDetails,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();

  const isExpired = batchDetails?.expiry_date
    ? dayjs(batchDetails.expiry_date).isBefore(dayjs())
    : false;
  const isExpiring = batchDetails?.expiry_date
    ? dayjs(batchDetails.expiry_date).isBefore(dayjs().add(30, "day"))
    : false;

  // Calculate unit conversions and stock information
  const { stockInfo, alternativeQuantity, stockPercentage, stockStatus } =
    useMemo(() => {
      if (
        !ingredientDetails ||
        ingredientDetails.stock_quantity === undefined
      ) {
        return {
          stockInfo: null,
          alternativeQuantity: null,
          stockPercentage: null,
          stockStatus: "unknown",
        };
      }

      // Convert ingredient quantity to stock unit if different
      const convertedQuantity =
        ingredient.unit !== ingredientDetails.unit &&
        areUnitsCompatible(ingredient.unit, ingredientDetails.unit)
          ? convertUnits(
              ingredient.quantity,
              ingredient.unit,
              ingredientDetails.unit
            )
          : ingredient.unit === ingredientDetails.unit
          ? ingredient.quantity
          : null;

      // Calculate stock percentage if units are compatible
      const stockPercentage =
        convertedQuantity !== null
          ? (convertedQuantity / ingredientDetails.stock_quantity) * 100
          : null;

      // Determine stock status
      let stockStatus: "critical" | "low" | "good" | "unknown" = "unknown";
      if (stockPercentage !== null) {
        if (stockPercentage > 100) {
          stockStatus = "critical";
        } else if (stockPercentage > 80) {
          stockStatus = "low";
        } else {
          stockStatus = "good";
        }
      }

      // Get alternative unit display if available
      const alternativeQuantity = suggestAlternativeUnit(
        ingredient.quantity,
        ingredient.unit
      );

      return {
        stockInfo:
          convertedQuantity !== null
            ? `${formatUnitValue(
                convertedQuantity,
                ingredientDetails.unit
              )} of ${formatUnitValue(
                ingredientDetails.stock_quantity,
                ingredientDetails.unit
              )}`
            : null,
        alternativeQuantity,
        stockPercentage,
        stockStatus,
      };
    }, [ingredient, ingredientDetails]);

  return (
    <Card
      variant="outlined"
      sx={{
        position: "relative",
        "&:hover": {
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      <CardContent sx={{ p: "12px !important" }}>
        <Stack spacing={1}>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Icon color="primary" fontSize="small" sx={{ opacity: 0.7 }}>
              {ingredient.item_type === "ingredient"
                ? "restaurant"
                : "inventory_2"}
            </Icon>
            <Typography
              variant="subtitle2"
              sx={{ flex: 1, color: "text.primary" }}
            >
              {ingredient?.brand_name || "Unknown Ingredient"}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={onEdit}
                sx={{ color: theme.palette.primary.main }}
              >
                <Icon fontSize="small">edit</Icon>
              </IconButton>
              <IconButton
                size="small"
                onClick={onDelete}
                sx={{ color: theme.palette.error.main }}
              >
                <Icon fontSize="small">delete</Icon>
              </IconButton>
            </Box>
          </Box>

          {/* Quantity and Unit */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Chip
              label={formatUnitValue(ingredient.quantity, ingredient.unit)}
              size="small"
              color="primary"
              variant="outlined"
            />
            {alternativeQuantity && (
              <Tooltip title="Alternative unit">
                <Chip
                  label={formatUnitValue(
                    alternativeQuantity.value,
                    alternativeQuantity.unit
                  )}
                  size="small"
                  color="default"
                  variant="outlined"
                />
              </Tooltip>
            )}
            {stockInfo && (
              <Tooltip title="Available stock">
                <Chip
                  label={stockInfo}
                  size="small"
                  color={
                    stockStatus === "critical"
                      ? "error"
                      : stockStatus === "low"
                      ? "warning"
                      : "success"
                  }
                  variant="outlined"
                  icon={
                    <Icon fontSize="small">
                      {stockStatus === "critical"
                        ? "error"
                        : stockStatus === "low"
                        ? "warning"
                        : "check_circle"}
                    </Icon>
                  }
                />
              </Tooltip>
            )}
          </Box>

          {/* Batch Information */}
          {batchDetails && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 0.5,
              }}
            >
              <Icon
                fontSize="small"
                color={isExpired ? "error" : isExpiring ? "warning" : "success"}
                sx={{ opacity: 0.7 }}
              >
                {isExpired ? "error" : isExpiring ? "warning" : "check_circle"}
              </Icon>
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Batch #{batchDetails.batch_number}
                    {batchDetails.brand_name && ` - ${batchDetails.brand_name}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={
                      isExpired
                        ? "error"
                        : isExpiring
                        ? "warning.main"
                        : "text.secondary"
                    }
                  >
                    Expires:{" "}
                    {dayjs(batchDetails.expiry_date).format("MMM D, YYYY")}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {batchDetails.remaining_quantity}/{batchDetails.quantity}{" "}
                    {ingredient.unit} remaining
                  </Typography>
                  <Typography variant="caption" color="primary">
                    {batchDetails.cost_per_unit.toFixed(3)} BHD/
                    {ingredient.unit}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}

          {/* Notes */}
          {ingredient.notes && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon fontSize="small" sx={{ color: "text.secondary" }}>
                notes
              </Icon>
              <Typography variant="caption" color="text.secondary">
                {ingredient.notes}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
